import { StreamErrors } from '@audacy-clients/client-services/core';
import type AudioServices from '@audacy-clients/client-services/src/audioServices';
import { PLAYBACK_RATES } from '@audacy-clients/client-services/src/audioServices/players/constants';
import {
  type IStreamerMachineContext,
  type IStreamerState,
} from '@audacy-clients/client-services/src/audioServices/streamers/types';
import {
  type TPlayableObject,
  ESleepTimerState,
  type IAudioEventListenerContext,
} from '@audacy-clients/client-services/src/audioServices/types';
import { shouldEnablePlaybackRates } from '@audacy-clients/client-services/src/utils/playbackRate';
import {
  chapterIndexState,
  audioServicesProvidedChapters,
} from '@audacy-clients/core/atoms/chapters';
import {
  activeCollectionState,
  collectionPlaybackModeState,
} from '@audacy-clients/core/atoms/collections';
import { useFeatureFlag } from '@audacy-clients/core/atoms/config/settings';
import { playbackResumePointsState } from '@audacy-clients/core/atoms/playbackResumePoints';
import playerState, {
  playerMetadataState,
  playerTimeState,
  playerVolumeState,
  playerSpeedState,
  playerLoadingState,
  type IPlayerState,
} from '@audacy-clients/core/atoms/player';
import { sleepUntilAtom, sleepUntilEndOfEpisodeAtom } from '@audacy-clients/core/atoms/sleepTimer';
import { wrapChapters } from '@audacy-clients/core/atoms/wrappers/chapter';
import { wrapContentObject } from '@audacy-clients/core/atoms/wrappers/content';
import { wrapEpisode } from '@audacy-clients/core/atoms/wrappers/episode';
import { wrapStandaloneChapter } from '@audacy-clients/core/atoms/wrappers/standaloneChapter';
import { PlayerState } from '@audacy-clients/core/types/player';
// import { NOT_AVAILABLE } from '@audacy-clients/core/constants/player';
import { useClientServices } from '@audacy-clients/core/utils/clientServices';
import { unescapeString } from '@audacy-clients/core/utils/strings';
import { useCallback, useRef, useEffect } from 'react';
import {
  type RecoilState,
  useRecoilTransaction_UNSTABLE,
  useSetRecoilState,
  useResetRecoilState,
} from 'recoil';
import { type StateValue } from 'xstate';

import { isDefined, useGetContextDiff } from './utils';

// TODO: [CCS-1391] Add case for Disabled?
// TODO: [CCS-1392] Figure out the constant approach / type sharing approach
const mapCSPlayerStateToAppPlayerState = (csState: StateValue): PlayerState => {
  switch (csState) {
    case 'PLAY_REQUESTED':
    case 'SKIP_REQUESTED':
    case 'LOADING':
    case 'FAILURE_WITH_RETRY':
    // [OOP-734] The exponential-backoff wait between retry attempts (up to
    // ~30s). The machine is still recovering, not failed — without this case
    // it fell to the default Error mapping and the user saw an error screen
    // while retries were in flight. Terminal exhaustion lands in FAILURE,
    // which correctly maps to Error below.
    case 'WAITING_TO_RETRY':
    case 'BUFFERING':
    case 'TRANSITION_TO_BUFFERING':
      return PlayerState.Loading;
    case 'PLAYING':
      return PlayerState.Playing;
    case 'PAUSED':
    case 'PAUSE_REQUESTED':
    case 'LOADED':
      return PlayerState.Paused;
    case 'ENDED':
      return PlayerState.Ended;
    case 'DESTROYED':
      return PlayerState.Idle;
    case 'FAILURE':
    default:
      return PlayerState.Error;
  }
};

type IPlayerStateConnectorProps = {
  onPlaybackError?: (event: unknown) => void; // TODO: [CCS-933] fix and change event type
  resetPlayerStateOnError?: boolean;
  onEvent?: (event: unknown) => void; // TODO: [CCS-933] fix and change event type
};

const PlayerStateConnector = ({
  onPlaybackError,
  resetPlayerStateOnError,
  onEvent,
}: IPlayerStateConnectorProps): null => {
  const diffAudioServicesContext = useGetContextDiff<IAudioEventListenerContext>();

  const lastAudioServicesStateRef = useRef<StateValue>();

  const lastStreamerStateRef = useRef<IStreamerState<TPlayableObject>['value']>();

  const diffStreamerContext = useGetContextDiff<IStreamerMachineContext<TPlayableObject>>();

  const isChaptersEnabled = useFeatureFlag('chapters');
  const resetPlayerState = useResetRecoilState(playerState);
  const resetPlayerTimeState = useResetRecoilState(playerTimeState);

  const handleError = useCallback(
    (event: unknown) => {
      if (resetPlayerStateOnError) {
        resetPlayerState();
        resetPlayerTimeState();
      }

      if (onPlaybackError) {
        onPlaybackError(event);
      }
    },
    [onPlaybackError, resetPlayerState, resetPlayerTimeState, resetPlayerStateOnError],
  );

  const handleEvent: Parameters<AudioServices['setEventListener']>[0] =
    useRecoilTransaction_UNSTABLE(
      ({ set, reset }) => {
        const cSet =
          <T,>(state: RecoilState<T>) =>
          (c: ((currVal: T) => T) | T) =>
            set(state, c);

        const setPlayerMetadataState = cSet(playerMetadataState);
        const setPlayerState = cSet(playerState);
        const setPlayerTimeState = cSet(playerTimeState);
        const setPlayerSpeedState = cSet(playerSpeedState);
        const setChapterIndexState = cSet(chapterIndexState);
        const resetChapterIndexState = () => reset(chapterIndexState);
        const setPlayerVolumeState = cSet(playerVolumeState);
        const setPlayerLoadingState = cSet(playerLoadingState);
        const setSleepUntilAtom = cSet(sleepUntilAtom);
        const setSleepUntilEndOfEpisodeAtom = cSet(sleepUntilEndOfEpisodeAtom);
        const setPlaybackResumePointsState = cSet(playbackResumePointsState);
        const setActiveCollection = cSet(activeCollectionState);
        const setCollectionPlaybackModeState = cSet(collectionPlaybackModeState);

        return ({ audioServicesState, streamerState }) => {
          const changedAudioServicesContextProperties = diffAudioServicesContext(
            audioServicesState.context,
          );

          const audioServicesStateValueChanged =
            audioServicesState.value.MAIN !== lastAudioServicesStateRef.current;
          lastAudioServicesStateRef.current = audioServicesState.value.MAIN;

          if (audioServicesStateValueChanged && audioServicesState.value.MAIN === 'FAILURE') {
            setPlayerLoadingState(false);
            // Using setTimeout to call handleError in a separate thread
            // so as not to violate Recoil's prohibition against updating an atom within
            // the execution of a state updater function
            setTimeout(
              () =>
                handleError({
                  type: 'error',
                  error: StreamErrors.UNKNOWN_ERROR,
                }),
              10,
            );
          }

          if (
            changedAudioServicesContextProperties.volume ||
            changedAudioServicesContextProperties.isMuted
          ) {
            setPlayerVolumeState((oldState) => ({
              ...oldState,
              volume: audioServicesState.context.volume,
              isMuted: audioServicesState.context.isMuted,
            }));
          }

          if (changedAudioServicesContextProperties.rate) {
            setPlayerSpeedState((prevState) => ({
              ...prevState,
              currentRate: audioServicesState.context.rate,
            }));
          }

          if (changedAudioServicesContextProperties.playbacks) {
            setPlaybackResumePointsState(audioServicesState.context.playbacks);
          }

          if (changedAudioServicesContextProperties.collectionPlaybackMode) {
            setCollectionPlaybackModeState(audioServicesState.context.collectionPlaybackMode);
          }

          if (changedAudioServicesContextProperties.activeCollection) {
            setActiveCollection(audioServicesState.context.activeCollection);
          }

          // Everything below this line has all streamer state logic

          const { value, context: streamerContext } = streamerState ?? {};

          const streamerStateValueChanged = value !== lastStreamerStateRef.current;
          lastStreamerStateRef.current = value;

          if (!value || !streamerContext) {
            return;
          }

          const changedStreamerContextProperties = diffStreamerContext(streamerContext);

          if (
            changedStreamerContextProperties.metadata ||
            changedStreamerContextProperties.episode ||
            changedStreamerContextProperties.item ||
            changedStreamerContextProperties.skipsCount
          ) {
            const metadata = streamerContext.metadata;

            // Handle Recoil state update first
            setPlayerMetadataState({
              ...metadata,
              skips: metadata?.skips,
              songOrShow: unescapeString(metadata?.songOrShow),
              station: unescapeString(metadata?.station),
              dataObject: wrapContentObject(streamerContext.item),
              episodeDataObject: streamerContext.episode
                ? wrapEpisode(streamerContext.episode)
                : undefined,
              standaloneChapterDataObject: streamerContext.standaloneChapter
                ? wrapStandaloneChapter(streamerContext.standaloneChapter)
                : undefined,
            });
          }

          if (changedStreamerContextProperties.currentChapterIndex) {
            if (streamerContext.currentChapterIndex !== undefined) {
              setChapterIndexState(streamerContext.currentChapterIndex);
            } else {
              resetChapterIndexState();
            }
          }

          if (
            changedStreamerContextProperties.chapters &&
            streamerContext.episode &&
            isChaptersEnabled
          ) {
            const atom = audioServicesProvidedChapters(streamerContext.episode.getId());
            if (streamerContext.chapters) {
              set(atom, wrapChapters(streamerContext.chapters));
            } else {
              reset(atom);
            }
          }

          if (streamerStateValueChanged) {
            setPlayerLoadingState(value === 'LOADING'); // TODO: [CCS-1392] figure out approach to constants / type checking for state values
          }

          if (streamerStateValueChanged || changedStreamerContextProperties.contentType) {
            setPlayerState((oldState: IPlayerState) => ({
              ...oldState,
              contentType: streamerContext.contentType,
              playState: mapCSPlayerStateToAppPlayerState(value), // TODO: [CCS-1392] remove eventually
            }));
          }

          if (streamerStateValueChanged && value === 'FAILURE') {
            setPlayerLoadingState(false);
            // Using setTimeout to call handleError in a separate thread
            // so as not to violate Recoil's prohibition against updating an atom within
            // the execution of a state updater function
            setTimeout(
              () =>
                handleError({
                  type: 'error',
                  error: StreamErrors.UNKNOWN_ERROR,
                  msg: 'Playback failed',
                }),
              10,
            );
          }

          if (changedStreamerContextProperties.contentType) {
            setPlayerSpeedState((prevState) => ({
              ...prevState,
              availableRates: shouldEnablePlaybackRates(
                audioServicesState.context.activePlayableObject?.getEntityType(),
                audioServicesState.context.activePlayableObject?.getEntitySubtype(),
              )
                ? PLAYBACK_RATES
                : [],
            }));
          }

          if (
            changedStreamerContextProperties.elapsed ||
            changedStreamerContextProperties.metadata ||
            streamerContext.metadata?.isLive
          ) {
            const { metadata, episode } = streamerContext;
            let { elapsed } = streamerContext;
            let duration = metadata?.duration;
            const start = metadata?.start;
            const stop = metadata?.stop;

            // [live] Anchor an on-air broadcast to the full scheduled episode
            // [start → stop] instead of the chapter-timeline sum. Otherwise the
            // machine's chapter-sum `duration` + live `elapsed` (last real
            // chapter end) pin the playhead to the right and read the play time
            // as the last chapter end, and the scheduled end label mismatches
            // the domain. At the live edge the playhead tracks wall clock; when
            // rewound it keeps the machine's show-relative position on the same
            // domain.
            if (episode?.isLiveOnAir?.() && isDefined(start) && isDefined(stop) && stop > start) {
              duration = (stop - start) / 1000;
              if (metadata?.isLive) {
                elapsed = Math.min(elapsed ?? 0, duration);
              }
            }

            let progress =
              isDefined(duration) && isDefined(elapsed) ? elapsed / duration : undefined;

            // prevents scrub bar from paintaing over 100%
            if (progress && progress > 1) {
              progress = 1;
            }

            let remaining =
              isDefined(duration) && isDefined(elapsed) ? duration - elapsed : undefined;

            // ensures the scrub bar time values don't keep ticking after it has finished
            if (remaining && remaining < 0) {
              remaining = 0;
            }
            if (elapsed && duration && elapsed > duration) {
              elapsed = duration;
            }

            const fewMinsAgo = Date.now() / 1000 - 120;

            // Fake live fraction of a few minutes ago due to SSR limitation
            let playbackLiveFraction =
              episode?.isLiveOnAir() && duration && start
                ? (fewMinsAgo - start / 1000) / duration
                : undefined;

// [OOP-738] The numerator is wall-clock time since the show start
            // (real broadcast timeline), but for MP3 rewind `duration` is the
            // chapter timeline (sum of chapter durations), so the raw ratio can
            // exceed 1 — painting the buffered/"live" bar past the end of the
            // scrubber and growing with wall-clock. It can also dip below 0 in
            // the first two minutes of a show. Clamp to [0, 1] like `progress`
            // above.
            if (isDefined(playbackLiveFraction)) {
              playbackLiveFraction = Math.min(Math.max(playbackLiveFraction, 0), 1);
            }

            // [OOP-741] MP3 rewind live-show scrubber input. Fraction of the
            // FULL scheduled episode covered by received MP3 content, so it
            // aligns with the scrubber's full-episode domain — the solid
            // available region then ends exactly where the scrubbable range
            // ends. Only computed once the on-air episode has actually started
            // (now is past its start time).
            const { availableContentEndOffset } = streamerContext;
            let availableContentFraction: number | undefined;
            if (
              isDefined(availableContentEndOffset) &&
              episode?.isLiveOnAir() &&
              start &&
              Date.now() / 1000 > start / 1000 &&
              isDefined(duration) &&
              duration > 0
            ) {
              availableContentFraction = Math.min(
                1,
                Math.max(0, availableContentEndOffset / duration),
              );
            }

            setPlayerTimeState({
              duration,
              offset: elapsed,
              remaining: remaining,
              playbackFraction: progress,
              playbackLiveFraction,
              availableContentFraction,
            });
          }

          if (changedAudioServicesContextProperties.sleepTimerState) {
            switch (audioServicesState.context.sleepTimerState) {
              case ESleepTimerState.Idle:
                setSleepUntilEndOfEpisodeAtom(false);
                setSleepUntilAtom(0);
                break;
              case ESleepTimerState.EndOfEpisode:
                setSleepUntilEndOfEpisodeAtom(true);
                break;
              default:
                setSleepUntilEndOfEpisodeAtom(false);
                setSleepUntilAtom(audioServicesState.context.sleepTimerState);
            }
          }
        };
      },
      [lastStreamerStateRef],
    );

  const { loading, clientServices } = useClientServices();
  const setPlayerVolumeState = useSetRecoilState(playerVolumeState);

  useEffect(() => {
    if (loading) {
      return;
    }
    const audioServices = clientServices.getAudioServices();
    setPlayerVolumeState({
      isMuted: audioServices.getMuted(),
      volume: audioServices.getVolume(),
    });
  }, [clientServices, loading, setPlayerVolumeState]);

  useEffect((): (() => void) | undefined => {
    if (loading) {
      return;
    }
    clientServices.getAudioServices().setEventListener(handleEvent);
    return () => clientServices.getAudioServices().setEventListener(() => {});
  }, [clientServices, handleEvent, loading, onEvent]);

  return null;
};

export default PlayerStateConnector;
